import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Row, Col, Select, Input, Table, Button, Tag, Space, message } from 'antd';
import { globalFormService, subCategoryGlobalFormService } from '@iso/services';
import {
  LIBRARY_FORM_TYPE_COLORS,
  getLibraryFormTypeLabel,
} from '@iso/constants/libraryFormTypes';
import SchemaPreviewModal from '@iso/containers/Pages/AdminPages/ManageLibraryForms/PreviewFormModal';

const SUCCESS_CODE = '0000';

/**
 * Browse published library templates and pick one to base a property form on.
 * Filters: Category (defaults to the Add Form category), Sub-category, search.
 * Uses getLivedForms (non-admin) and filters client-side.
 */
const SelectFromLibraryModal = ({
  visible,
  onCancel,
  onSelect,
  categories = [],
  initialCategoryId = null,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryId, setCategoryId] = useState(undefined);
  const [subCategoryId, setSubCategoryId] = useState(undefined);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState({ visible: false, form: null });

  const loadTemplates = useCallback(() => {
    setLoading(true);
    globalFormService
      .getLivedForms()
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setTemplates(res.global_forms || []);
        } else {
          message.error((res && res.message) || 'Failed to load templates');
        }
      })
      .catch(() => message.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  const loadSubCategories = useCallback((id) => {
    if (!id) {
      setSubCategoryOptions([]);
      return;
    }
    subCategoryGlobalFormService
      .get(id)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setSubCategoryOptions(
            (res.sub_category_global_forms || []).map((s) => ({
              value: s.id,
              label: s.name,
            }))
          );
        }
      })
      .catch(() => message.error('Failed to load sub-categories'));
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setCategoryId(initialCategoryId || undefined);
    setSubCategoryId(undefined);
    setSearch('');
    setPreview({ visible: false, form: null });
    loadTemplates();
  }, [visible, initialCategoryId, loadTemplates]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    loadSubCategories(categoryId);
  }, [visible, categoryId, loadSubCategories]);

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  );

  const filtered = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    return templates
      .filter((tpl) => {
        if (categoryId != null && Number(tpl.category_global_form_id) !== Number(categoryId)) {
          return false;
        }
        if (subCategoryId != null && Number(tpl.sub_category_global_form_id) !== Number(subCategoryId)) {
          return false;
        }
        if (term && !(tpl.name || '').toLowerCase().includes(term)) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      });
  }, [templates, categoryId, subCategoryId, search]);

  const columns = [
    {
      title: 'Template',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (value) =>
        value ? (
          <Tag color={LIBRARY_FORM_TYPE_COLORS[value]}>{getLibraryFormTypeLabel(value)}</Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (value) => value || '-',
    },
    {
      title: 'Sub-category',
      dataIndex: 'sub_category_name',
      key: 'sub_category_name',
      render: (value) => value || '-',
    },
    {
      title: '',
      key: 'select',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            onClick={() =>
              setPreview({
                visible: true,
                form: {
                  name: record.name,
                  type: record.type,
                  schema_json: record.schema_json,
                },
              })
            }
          >
            Preview
          </Button>
          <Button
            size="small"
            type="primary"
            className="btn-success"
            onClick={() => onSelect(record)}
          >
            Select
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Select from Library"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={8}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Category"
            value={categoryId}
            options={categoryOptions}
            onChange={(value) => {
              setCategoryId(value);
              setSubCategoryId(undefined);
            }}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Sub-category"
            value={subCategoryId}
            options={subCategoryOptions}
            onChange={setSubCategoryId}
            disabled={!categoryId}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Input.Search
            placeholder="Search templates"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={setSearch}
          />
        </Col>
      </Row>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={filtered}
        columns={columns}
        pagination={{ pageSize: 8 }}
        locale={{ emptyText: 'No templates found. Please try with different search terms.' }}
      />
      <SchemaPreviewModal
        visible={preview.visible}
        form={preview.form}
        onCancel={() => setPreview({ visible: false, form: null })}
      />
    </Modal>
  );
};

export default SelectFromLibraryModal;
